import { useState, useEffect, useContext, useCallback, useRef } from 'react'
import { __ } from '@wordpress/i18n'
import SbUtils from '../../Utils/SbUtils'
import Button from '../Common/Button'
import WooProductSelector from '../Common/WooProductSelector'
import useWooValidation from '../../hooks/useWooValidation'

/**
 * Edit WooCommerce Products Modal
 *
 * Allows editing the products included in a multi-product WooCommerce source.
 *
 * @param {Object} props
 * @param {Object} props.source - The source being edited
 * @param {Function} props.onCancel - Called when modal is cancelled
 * @param {Function} props.onSuccess - Called after successful update
 */
const EditWooProductsModal = ( props ) => {
    const {
        sbCustomizer,
        editorNotification,
        editorTopLoader
    } = useContext( SbUtils.getCurrentContext() );

    // Parse source info with error handling
    let sourceInfo = null;
    try {
        sourceInfo = typeof props.source.info === 'string'
            ? JSON.parse( props.source.info )
            : props.source.info;
    } catch ( e ) {
        console.error( 'Failed to parse source info:', e );
        sourceInfo = {};
    }

    // Products state
    const [ allProducts, setAllProducts ] = useState( [] );
    const [ selectedProducts, setSelectedProducts ] = useState( [] );
    const [ isLoading, setIsLoading ] = useState( true );
    const [ isSearchingProducts, setIsSearchingProducts ] = useState( false );

    // Categories state
    const [ allCategories, setAllCategories ] = useState( [] );
    const [ selectedCategories, setSelectedCategories ] = useState( [] );
    const [ loadingCategories, setLoadingCategories ] = useState( true );

    // Tags state
    const [ allTags, setAllTags ] = useState( [] );
    const [ selectedTags, setSelectedTags ] = useState( [] );
    const [ loadingTags, setLoadingTags ] = useState( true );

    // Common state
    const [ isSaving, setIsSaving ] = useState( false );
    const [ validationError, setValidationError ] = useState( null );
    const [ sourceName, setSourceName ] = useState( sourceInfo?.source_name || props.source.name || '' );

    // WooCommerce validation hook (handles zero reviews warning automatically)
    const { validateSelection } = useWooValidation( {
        selectedProducts,
        selectedCategories,
        selectedTags,
        setValidationError
    } );

    // Refs to track if initial selection has been done (prevents re-selection on search)
    const initialProductsSelected = useRef( false );
    const initialCategoriesSelected = useRef( false );
    const initialTagsSelected = useRef( false );

    // Load products, categories, and tags on mount
    useEffect( () => {
        loadProducts();
        loadCategories();
        loadTags();
    }, [] );

    // Pre-select products from source (only on initial load, not on search)
    useEffect( () => {
        if ( initialProductsSelected.current ) {
            return;
        }
        if ( allProducts.length > 0 && Array.isArray( sourceInfo?.direct_product_ids ) ) {
            // Use direct_product_ids (user-selected products) rather than all resolved product_ids
            const preSelected = allProducts.filter( product =>
                sourceInfo.direct_product_ids.includes( product.id )
            );
            setSelectedProducts( preSelected );
            initialProductsSelected.current = true;
        } else if ( allProducts.length > 0 && Array.isArray( sourceInfo?.product_ids ) ) {
            // Fallback for older sources that don't have direct_product_ids
            const preSelected = allProducts.filter( product =>
                sourceInfo.product_ids.includes( product.id )
            );
            setSelectedProducts( preSelected );
            initialProductsSelected.current = true;
        }
    }, [ allProducts ] );

    // Pre-select categories from source (only on initial load, not on search)
    useEffect( () => {
        if ( initialCategoriesSelected.current ) {
            return;
        }
        if ( allCategories.length > 0 && Array.isArray( sourceInfo?.category_ids ) ) {
            const preSelected = allCategories.filter( category =>
                sourceInfo.category_ids.includes( category.id )
            );
            setSelectedCategories( preSelected );
            initialCategoriesSelected.current = true;
        }
    }, [ allCategories ] );

    // Pre-select tags from source (only on initial load, not on search)
    useEffect( () => {
        if ( initialTagsSelected.current ) {
            return;
        }
        if ( allTags.length > 0 && Array.isArray( sourceInfo?.tag_ids ) ) {
            const preSelected = allTags.filter( tag =>
                sourceInfo.tag_ids.includes( tag.id )
            );
            setSelectedTags( preSelected );
            initialTagsSelected.current = true;
        }
    }, [ allTags ] );

    const loadProducts = useCallback( ( searchTerm = '' ) => {
        // Show skeleton loaders for both initial load and search
        setIsSearchingProducts( true );
        const startTime = Date.now();
        const minDisplayTime = 500; // Minimum time to show skeleton loaders (ms)

        const formData = {
            action: 'sbr_get_woocommerce_products',
            nonce: sbCustomizer?.nonce,
            search: searchTerm
        };

        SbUtils.ajaxPost(
            sbCustomizer.ajaxHandler,
            formData,
            ( response ) => {
                // Ensure skeleton shows for at least minDisplayTime
                const elapsed = Date.now() - startTime;
                const remainingTime = Math.max( 0, minDisplayTime - elapsed );

                setTimeout( () => {
                    setIsLoading( false );
                    setIsSearchingProducts( false );
                    if ( response?.success && response?.data?.products ) {
                        setAllProducts( response.data.products );
                    }
                }, remainingTime );
            },
            editorTopLoader,
            null,
            null
        );
    }, [ sbCustomizer?.ajaxHandler, sbCustomizer?.nonce, editorTopLoader ] );

    const loadCategories = () => {
        setLoadingCategories( true );
        const startTime = Date.now();
        const minDisplayTime = 500;

        const formData = {
            action: 'sbr_get_woocommerce_categories',
            nonce: sbCustomizer?.nonce
        };

        SbUtils.ajaxPost(
            sbCustomizer.ajaxHandler,
            formData,
            ( response ) => {
                const elapsed = Date.now() - startTime;
                const remainingTime = Math.max( 0, minDisplayTime - elapsed );

                setTimeout( () => {
                    setLoadingCategories( false );
                    if ( response?.success && response?.data?.categories ) {
                        setAllCategories( response.data.categories );
                    }
                }, remainingTime );
            },
            null,
            null,
            null
        );
    };

    const loadTags = () => {
        setLoadingTags( true );
        const startTime = Date.now();
        const minDisplayTime = 500;

        const formData = {
            action: 'sbr_get_woocommerce_tags',
            nonce: sbCustomizer?.nonce
        };

        SbUtils.ajaxPost(
            sbCustomizer.ajaxHandler,
            formData,
            ( response ) => {
                const elapsed = Date.now() - startTime;
                const remainingTime = Math.max( 0, minDisplayTime - elapsed );

                setTimeout( () => {
                    setLoadingTags( false );
                    if ( response?.success && response?.data?.tags ) {
                        setAllTags( response.data.tags );
                    }
                }, remainingTime );
            },
            null,
            null,
            null
        );
    };

    const handleProductToggle = ( product ) => {
        setValidationError( null );
        const isSelected = selectedProducts.some( p => p.id === product.id );
        if ( isSelected ) {
            setSelectedProducts( selectedProducts.filter( p => p.id !== product.id ) );
        } else {
            setSelectedProducts( [ ...selectedProducts, product ] );
        }
    };

    const handleCategoryToggle = ( category ) => {
        setValidationError( null );
        const isSelected = selectedCategories.some( c => c.id === category.id );
        if ( isSelected ) {
            setSelectedCategories( selectedCategories.filter( c => c.id !== category.id ) );
        } else {
            setSelectedCategories( [ ...selectedCategories, category ] );
        }
    };

    const handleTagToggle = ( tag ) => {
        setValidationError( null );
        const isSelected = selectedTags.some( t => t.id === tag.id );
        if ( isSelected ) {
            setSelectedTags( selectedTags.filter( t => t.id !== tag.id ) );
        } else {
            setSelectedTags( [ ...selectedTags, tag ] );
        }
    };

    const handleSelectAll = ( filteredProducts ) => {
        setValidationError( null );
        // Merge: add filtered products to existing selection (avoid duplicates)
        const existingIds = new Set( selectedProducts.map( p => p.id ) );
        const newProducts = filteredProducts.filter( p => !existingIds.has( p.id ) );
        setSelectedProducts( [ ...selectedProducts, ...newProducts ] );
    };

    const handleDeselectAll = ( filteredProducts ) => {
        // Only deselect filtered/visible products, preserve non-visible selections
        const filteredIds = new Set( filteredProducts.map( p => p.id ) );
        setSelectedProducts( selectedProducts.filter( p => !filteredIds.has( p.id ) ) );
    };

    const handleSelectAllCategories = ( filteredCategories ) => {
        setValidationError( null );
        // Merge: add filtered categories to existing selection (avoid duplicates)
        const existingIds = new Set( selectedCategories.map( c => c.id ) );
        const newCategories = filteredCategories.filter( c => !existingIds.has( c.id ) );
        setSelectedCategories( [ ...selectedCategories, ...newCategories ] );
    };

    const handleDeselectAllCategories = ( filteredCategories ) => {
        // Only deselect filtered/visible categories, preserve non-visible selections
        const filteredIds = new Set( filteredCategories.map( c => c.id ) );
        setSelectedCategories( selectedCategories.filter( c => !filteredIds.has( c.id ) ) );
    };

    const handleSelectAllTags = ( filteredTags ) => {
        setValidationError( null );
        // Merge: add filtered tags to existing selection (avoid duplicates)
        const existingIds = new Set( selectedTags.map( t => t.id ) );
        const newTags = filteredTags.filter( t => !existingIds.has( t.id ) );
        setSelectedTags( [ ...selectedTags, ...newTags ] );
    };

    const handleDeselectAllTags = ( filteredTags ) => {
        // Only deselect filtered/visible tags, preserve non-visible selections
        const filteredIds = new Set( filteredTags.map( t => t.id ) );
        setSelectedTags( selectedTags.filter( t => !filteredIds.has( t.id ) ) );
    };

    const handleClearAll = () => {
        setSelectedProducts( [] );
        setSelectedCategories( [] );
        setSelectedTags( [] );
    };

    const handleSave = () => {
        // Validate - at least one selection required
        const validationMsg = validateSelection();
        if ( validationMsg ) {
            setValidationError( validationMsg );
            return;
        }

        setIsSaving( true );

        // Build formData with arrays for PHP handling
        const formData = {
            action: 'sbr_update_woocommerce_source_multi',
            nonce: sbCustomizer?.nonce,
            source_id: props.source.account_id,
            source_name: sourceName || __( 'WooCommerce Products', 'sb-customizer' )
        };

        // Add product_ids as separate entries for PHP array handling
        selectedProducts.forEach( ( product, index ) => {
            formData[ 'product_ids[' + index + ']' ] = product.id;
        });

        // Add category_ids as separate entries for PHP array handling
        selectedCategories.forEach( ( category, index ) => {
            formData[ 'category_ids[' + index + ']' ] = category.id;
        });

        // Add tag_ids as separate entries for PHP array handling
        selectedTags.forEach( ( tag, index ) => {
            formData[ 'tag_ids[' + index + ']' ] = tag.id;
        });

        SbUtils.ajaxPost(
            sbCustomizer.ajaxHandler,
            formData,
            ( response ) => {
                setIsSaving( false );
                if ( response?.success ) {
                    // Don't update sources list from response - parent will refresh for current page
                    // This prevents showing page 1 results while staying on current page

                    SbUtils.applyNotification( {
                        icon: 'success',
                        text: __( 'Source updated successfully', 'sb-customizer' )
                    }, editorNotification );

                    if ( props.onSuccess ) {
                        props.onSuccess( response.source );
                    }
                } else {
                    SbUtils.applyNotification( {
                        icon: 'error',
                        text: response?.message || __( 'Failed to update source', 'sb-customizer' )
                    }, editorNotification );
                }
            },
            editorTopLoader,
            null,
            null
        );
    };

    return (
        <div className='sb-addsource-modal sb-edit-woo-modal sb-fs'>
            <div className='sb-addsource-modal-heading sb-fs'>
                <h4 className='sb-h4'>{ __( 'Edit Products', 'sb-customizer' ) }</h4>
            </div>

            <div className='sb-addsource-modal-content sb-fs'>
                {/* Source Name */}
                <div className='sb-woo-source-name-section'>
                    <label className='sb-text-small sb-bold'>{ __( 'Source Name', 'sb-customizer' ) }</label>
                    <input
                        type='text'
                        className='sb-woo-source-name-input'
                        placeholder={ __( 'Enter a name for this source', 'sb-customizer' ) }
                        value={ sourceName }
                        onChange={ ( e ) => setSourceName( e.target.value ) }
                    />
                </div>

                {/* Shared Product Selector Component with Tabs */}
                <WooProductSelector
                    showTabs={ true }
                    products={ allProducts }
                    selectedProducts={ selectedProducts }
                    onProductToggle={ handleProductToggle }
                    onSelectAll={ handleSelectAll }
                    onDeselectAll={ handleDeselectAll }
                    isLoading={ isLoading }
                    validationError={ validationError }
                    categories={ allCategories }
                    selectedCategories={ selectedCategories }
                    onCategoryToggle={ handleCategoryToggle }
                    onSelectAllCategories={ handleSelectAllCategories }
                    onDeselectAllCategories={ handleDeselectAllCategories }
                    loadingCategories={ loadingCategories }
                    tags={ allTags }
                    selectedTags={ selectedTags }
                    onTagToggle={ handleTagToggle }
                    onSelectAllTags={ handleSelectAllTags }
                    onDeselectAllTags={ handleDeselectAllTags }
                    loadingTags={ loadingTags }
                    onClearAll={ handleClearAll }
                    onSearchProducts={ loadProducts }
                    isSearching={ isSearchingProducts }
                />
            </div>

            <div className='sb-addsource-modal-btns sb-fs'>
                <Button
                    size='medium'
                    type='secondary'
                    text={ __( 'Cancel', 'sb-customizer' ) }
                    onClick={ props.onCancel }
                />
                <Button
                    size='medium'
                    type='primary'
                    text={ __( 'Finish', 'sb-customizer' ) }
                    icon='chevron-right'
                    iconPosition='right'
                    onClick={ handleSave }
                />
            </div>
        </div>
    );
};

export default EditWooProductsModal;
